import { useState, useEffect } from "react";
import { MessageSquare, ShoppingBag, AlertTriangle, TrendingUp } from "lucide-react";
import { useMetrics, useConversations, useOrders } from "../hooks/useData";
import { supabase } from "../supabase";

const WHATSAPP_NUMBER = "212663820033";
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}?text=START`;

const channelConfig = {
  whatsapp: { color: "bg-green-500", label: "WA" },
  instagram: { color: "bg-pink-500", label: "IG" },
  facebook:  { color: "bg-blue-600", label: "FB" },
};

const stateConfig = {
  escalate:        { label: "Escalade",    color: "bg-red-100 text-red-800",      reply: "⚠ Intervention requise",    replyColor: "text-red-600" },
  payment_pending: { label: "Paiement",    color: "bg-amber-100 text-amber-800",  reply: "⏳ En attente de paiement", replyColor: "text-amber-600" },
  payment_sent:    { label: "À confirmer", color: "bg-amber-100 text-amber-800",  reply: "💳 Preuve envoyée",          replyColor: "text-amber-600" },
  confirmed:       { label: "Confirmé",    color: "bg-green-100 text-green-800",  reply: "✓ Commande confirmée",       replyColor: "text-green-600" },
  discovery:       { label: "Nouveau",     color: "bg-gray-100 text-gray-800",    reply: "✓ Hellodia a répondu",       replyColor: "text-green-600" },
  interested:      { label: "Intéressé",   color: "bg-orange-100 text-orange-800",reply: "✓ Hellodia a répondu",       replyColor: "text-green-600" },
};

function formatRevenue(amount) {
  if (amount >= 1000) return `${(amount / 1000).toFixed(0)}k`;
  return amount.toString();
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / 60000);
  if (diff < 1) return "À l'instant";
  if (diff < 60) return `${diff}min`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h`;
  return `${Math.floor(diff / 1440)}j`;
}

function TrialExpiredBanner({ onGoToSettings }) {
  return (
    <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-xl flex-shrink-0">⏰</span>
        <div>
          <div className="text-white font-medium text-sm">
            Votre période d'essai est terminée
          </div>
          <div className="text-red-100 text-xs">
            Hellodia ne répond plus à vos clients — choisissez un plan pour continuer.
          </div>
        </div>
      </div>
      <button
        onClick={onGoToSettings}
        className="flex-shrink-0 px-3 py-2 bg-white text-red-600 rounded-xl text-xs font-medium hover:bg-red-50 transition-colors whitespace-nowrap"
      >
        Choisir un plan →
      </button>
    </div>
  );
}

function OnboardingBanner({ missing, started }) {
  const [pulse, setPulse] = useState(true);

  useEffect(() => {
    const iv = setInterval(() => setPulse(p => !p), 1500);
    return () => clearInterval(iv);
  }, []);

  if (!started) {
    return (
      <div className={`rounded-xl p-4 transition-all duration-700 ${pulse ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gradient-to-r from-green-600 to-green-700'}`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-xl">👋</span>
            </div>
            <div>
              <div className="text-white font-medium text-sm mb-0.5">
                Configurez votre assistant Hellodia
              </div>
              <div className="text-green-100 text-xs">
                Envoyez <strong className="text-white bg-white/20 px-1.5 py-0.5 rounded">START</strong> à Hellodia sur WhatsApp pour démarrer la configuration en 5 minutes.
              </div>
            </div>
          </div>
          <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer"
            className="flex-shrink-0 px-3 py-2 bg-white text-green-700 rounded-xl text-xs font-medium hover:bg-green-50 transition-colors whitespace-nowrap">
            💬 Configurer maintenant
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl p-4 transition-all duration-700 ${pulse ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-amber-600 to-orange-600'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-xl">⚙️</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-white font-medium text-sm mb-2">
              Configuration incomplète — il manque encore :
            </div>
            <div className="space-y-1 mb-3">
              {missing.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-orange-100">
                  <span className="text-red-200">✗</span>
                  {item}
                </div>
              ))}
            </div>
            <div className="text-orange-100 text-xs">
              Envoyez <strong className="text-white bg-white/20 px-1.5 py-0.5 rounded">START</strong> sur WhatsApp pour continuer.
            </div>
          </div>
        </div>
        <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer"
          className="flex-shrink-0 px-3 py-2 bg-white text-orange-600 rounded-xl text-xs font-medium hover:bg-orange-50 transition-colors whitespace-nowrap">
          💬 Continuer
        </a>
      </div>
    </div>
  );
}

export default function Home() {
  const { metrics, loading: metricsLoading } = useMetrics();
  const { conversations, loading: convsLoading } = useConversations();
  const { orders, loading: ordersLoading, confirmOrder, rejectOrder } = useOrders();
  const [missingItems, setMissingItems] = useState(null);
  const [currency, setCurrency] = useState('FCFA');
  const [trialExpired, setTrialExpired] = useState(false);

  useEffect(() => {
    async function checkOnboarding() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('organizations')
        .select('id, is_trial, agent_name, phone, currency, trial_expires_at, business_type, whatsapp_phone_id')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setCurrency(data.currency || 'FCFA');

        // Vérifier trial expiré
        if (data.is_trial && data.trial_expires_at) {
          const expired = new Date() > new Date(data.trial_expires_at);
          if (expired) {
            setTrialExpired(true);
            return; // Pas besoin de vérifier l'onboarding si trial expiré
          }
        }

        const { data: products } = await supabase
          .from('products')
          .select('id')
          .eq('organization_id', data.id)
          .limit(1);

        const started = !!data.agent_name;
        const missing = [];
        if (!data.business_type) missing.push("Type de business non configuré");
        if (!data.phone && !data.whatsapp_phone_id) missing.push("Numéro WhatsApp non connecté");
        if (!products || products.length === 0) missing.push("Aucun produit ou service ajouté");

        setMissingItems({ missing, started });
      }
    }
    checkOnboarding();
  }, []);

  const metricCards = [
    { label: "Messages traités aujourd'hui", value: metrics.messagesTotal, sub: "Répondus automatiquement ✓", subColor: "text-green-600", icon: MessageSquare, iconBg: "bg-orange-50", iconColor: "text-orange-600" },
    { label: "Commandes en attente", value: metrics.ordersPending, sub: "À confirmer", subColor: "text-amber-600", icon: ShoppingBag, iconBg: "bg-amber-50", iconColor: "text-amber-600" },
    { label: "Escalades", value: metrics.escalades, sub: "Intervention requise", subColor: "text-red-600", icon: AlertTriangle, iconBg: "bg-red-50", iconColor: "text-red-600" },
    { label: "Revenus du mois", value: `${formatRevenue(metrics.revenue)} ${currency}`, sub: "Commandes confirmées", subColor: "text-green-600", icon: TrendingUp, iconBg: "bg-green-50", iconColor: "text-green-600" },
  ];

  const pendingOrders = orders.filter(o => o.status === 'pending');

  return (
    <div className="space-y-6">

      {trialExpired ? (
        <TrialExpiredBanner onGoToSettings={() => {}} />
      ) : (
        missingItems && (missingItems.missing.length > 0 || !missingItems.started) && (
          <OnboardingBanner missing={missingItems.missing} started={missingItems.started} />
        )
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {metricCards.map((m, i) => {
          const Icon = m.icon;
          return (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-gray-500">{m.label}</span>
                <div className={`w-8 h-8 rounded-lg ${m.iconBg} flex items-center justify-center`}>
                  <Icon size={16} className={m.iconColor} />
                </div>
              </div>
              <div className="text-2xl font-medium text-gray-800">
                {metricsLoading ? "..." : m.value}
              </div>
              <div className={`text-xs mt-1 ${m.subColor}`}>{m.sub}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-800">Derniers messages</span>
            <span className="text-xs text-orange-600 cursor-pointer">Voir tout</span>
          </div>
          {convsLoading ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400">Chargement...</div>
          ) : conversations.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400">Aucune conversation</div>
          ) : (
            conversations.slice(0, 5).map((conv, i) => {
              const state = conv.conversation_states?.[0]?.current_state || 'discovery';
              const stateInfo = stateConfig[state] || stateConfig.discovery;
              const channel = conv.channel || 'whatsapp';
              const channelInfo = channelConfig[channel] || channelConfig.whatsapp;
              const lastMsg = conv.messages?.[conv.messages.length - 1];
              const initials = (conv.contacts?.phone || '??').slice(-2).toUpperCase();

              return (
                <div key={i} className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center text-xs font-medium text-orange-800">
                        {initials}
                      </div>
                      <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 ${channelInfo.color} rounded-full border-2 border-white flex items-center justify-center text-white`} style={{fontSize:'6px'}}>
                        {channelInfo.label}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-800">{conv.contacts?.phone || 'Inconnu'}</span>
                        <span className="text-xs text-gray-400">{timeAgo(lastMsg?.created_at)}</span>
                      </div>
                      <div className="text-xs text-gray-500 truncate mt-0.5">{lastMsg?.content || '...'}</div>
                      <div className={`text-xs mt-1 ${stateInfo.replyColor}`}>{stateInfo.reply}</div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${stateInfo.color}`}>
                      {stateInfo.label}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-medium text-gray-800">Commandes à confirmer</span>
            <span className="text-xs text-orange-600 cursor-pointer">Voir tout</span>
          </div>
          {ordersLoading ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400">Chargement...</div>
          ) : pendingOrders.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400">Aucune commande en attente</div>
          ) : (
            pendingOrders.map((o, i) => (
              <div key={i} className="px-4 py-3 border-b border-gray-50 last:border-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-800">{o.contacts?.phone || 'Client'}</span>
                  <span className="text-sm font-medium text-orange-600">{o.amount?.toLocaleString()} {currency}</span>
                </div>
                <div className="text-xs text-gray-500 mb-2">{o.payment_method || 'Paiement mobile'}</div>
                <div className="flex gap-2">
                  <button onClick={() => confirmOrder(o.id)} className="px-3 py-1 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-medium hover:bg-green-100">
                    Confirmer
                  </button>
                  <button onClick={() => rejectOrder(o.id)} className="px-3 py-1 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100">
                    Refuser
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

      </div>
    </div>
  );
}